const hasFieldValue = (value) => String(value ?? '').trim() !== '';

const normalizeRoleToken = (value) => {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) {
    return '';
  }

  if (normalized === 'gnd' || normalized.includes('ground')) {
    return 'ground';
  }

  if (
    normalized.includes('power')
    || normalized === 'vcc'
    || normalized === 'vdd'
    || normalized === 'vin'
    || normalized === 'vbat'
    || normalized === 'vbatt'
    || normalized === '3v3'
    || normalized === '3.3v'
    || normalized === '5v'
    || normalized === '5.0v'
  ) {
    return 'power';
  }

  return normalized;
};

const parseCapabilityTags = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeRoleToken).filter(Boolean);
  }

  const text = String(value || '').trim();
  if (!text) {
    return [];
  }

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map(normalizeRoleToken).filter(Boolean);
    }
  } catch (error) {
    // Fall back to comma-separated tags.
  }

  return text
    .split(',')
    .map(normalizeRoleToken)
    .filter(Boolean);
};

const normalizePinNameToken = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/^\+/, '')
  .replace(/[\s_.-]/g, '');

const inferPowerRoleFromPinName = (pin) => {
  const nameValues = [
    pin?.pin_name,
    pin?.pinName,
    pin?.name,
  ].filter(Boolean);

  for (const nameValue of nameValues) {
    const nameParts = String(nameValue)
      .split(/[/:,|()[\]\s]+/)
      .map(normalizePinNameToken)
      .filter(Boolean);

    if (nameParts.some((part) => /^(gnd|ground|vss)\d*$/.test(part))) {
      return 'ground';
    }

    if (nameParts.some((part) => /^(vcc|vdd|vin|vbat|vbatt|vbus|vsys|vusb|raw|vraw|3v3|33v|5v|50v)\d*$/.test(part))) {
      return 'power';
    }
  }

  return '';
};

const inferPowerRoleFromGroup = (pin) => {
  const groupText = String(pin?.group_name ?? pin?.groupName ?? '').trim().toLowerCase();

  if (groupText.includes('ground')) {
    return 'ground';
  }

  if (groupText.includes('power')) {
    return 'power';
  }

  return '';
};

export const isPowerPinForElectricalDefaults = (pin) => {
  const explicitRole = normalizeRoleToken(pin?.pin_role ?? pin?.pinRole);
  if (explicitRole === 'power' || explicitRole === 'ground') {
    return true;
  }

  const capabilityTags = parseCapabilityTags(pin?.capability_tags ?? pin?.capabilityTags);
  if (capabilityTags.includes('power') || capabilityTags.includes('ground')) {
    return true;
  }

  const inferredRole = inferPowerRoleFromPinName(pin) || inferPowerRoleFromGroup(pin);
  return inferredRole === 'power' || inferredRole === 'ground';
};

export const hasCompleteBulkElectricalValues = (values) => (
  hasFieldValue(values?.current_limit_ma)
  && hasFieldValue(values?.voltage_min_v)
  && hasFieldValue(values?.voltage_max_v)
);

export const applyBulkElectricalValuesToPins = (pins, values) => {
  if (!Array.isArray(pins) || !hasCompleteBulkElectricalValues(values)) {
    return Array.isArray(pins) ? pins : [];
  }

  return pins.map((pin) => {
    if (isPowerPinForElectricalDefaults(pin)) {
      return pin;
    }

    return {
      ...pin,
      current_limit_ma: values.current_limit_ma,
      voltage_min_v: values.voltage_min_v,
      voltage_max_v: values.voltage_max_v,
    };
  });
};

export const countEligibleBulkElectricalPins = (pins) => (
  Array.isArray(pins)
    ? pins.filter((pin) => !isPowerPinForElectricalDefaults(pin)).length
    : 0
);

export const createEmptyBulkElectricalValues = () => ({
  current_limit_ma: '',
  voltage_min_v: '',
  voltage_max_v: '',
});
